/**
 * Regulation Loader
 * Handles dynamic loading of country-specific regulations
 * Uses code splitting to only load the selected country's bundle
 */
import { devLog } from '../../utils/devLog';
import { metaKey } from '../../utils/metaKey';

// Cache loaded regulations to avoid re-importing
const regulationCache = {};

/**
 * Dynamically load regulations for a specific country
 * @param {string} countryCode - ISO country code (UK, US, AU, etc.)
 * @returns {Promise<object>} Country regulation module
 */
export async function loadCountryRegulations(countryCode = 'UK') {
  // Return from cache if already loaded
  if (regulationCache[countryCode]) {
    return regulationCache[countryCode];
  }

  try {
    let regulations;
    
    // Use dynamic imports with webpack chunk names for code splitting
    switch (countryCode) {
      case 'UK':
        regulations = await import(
          /* webpackChunkName: "regulations-uk" */
          './UK/index'
        );
        break;
        
      case 'US':
        // Ready for future US regulations
        regulations = await import(
          /* webpackChunkName: "regulations-us" */
          './US/index'
        );
        break;
        
      case 'AU':
        // Ready for future Australian regulations
        regulations = await import(
          /* webpackChunkName: "regulations-au" */
          './AU/index'
        );
        break;
        
      case 'CA':
        // Ready for future Canadian regulations
        regulations = await import(
          /* webpackChunkName: "regulations-ca" */
          './CA/index'
        );
        break;
        
      default:

        regulations = await import(
          /* webpackChunkName: "regulations-uk" */
          './UK/index'
        );
    }
    
    // Cache the loaded regulations
    regulationCache[countryCode] = regulations;
    
    // Store selected country (TODO: MIGRATE TO VESSEL-SCOPED META)
    /* MIGRATE_TO_COLLECTIONS */ localStorage.setItem('selectedCountry', countryCode);
    
    // Dispatch event for UI updates
    window.dispatchEvent(new CustomEvent('regulationsLoaded', { 
      detail: { 
        country: countryCode,
        metadata: regulations.countryMetadata
      } 
    }));
    
    return regulations;
    
  } catch (error) {

    // Fallback to UK if loading fails
    if (countryCode !== 'UK') {
      devLog('Falling back to UK regulations');
      return loadCountryRegulations('UK');
    }
    
    throw error;
  }
}

/**
 * Get currently selected country (TODO: MIGRATE TO VESSEL-SCOPED META)
 */
export function getCurrentCountry() {
  return /* MIGRATE_TO_COLLECTIONS */ localStorage.getItem('selectedCountry') || 'UK';
}

/**
 * Clear regulation cache (useful when switching countries)
 */
export function clearRegulationCache() {
  Object.keys(regulationCache).forEach(key => {
    delete regulationCache[key];
  });
}

/**
 * Preload regulations for a country (for faster switching)
 */
export async function preloadCountryRegulations(countryCode) {
  try {
    await loadCountryRegulations(countryCode);
    devLog(`Preloaded regulations for ${countryCode}`);
    return true;
  } catch (error) {

    return false;
  }
}

/**
 * Get list of available countries
 */
export function getAvailableCountries() {
  return [
    { code: 'UK', name: 'United Kingdom', flag: '🇬🇧', available: true },
    { code: 'US', name: 'United States', flag: '🇺🇸', available: false, coming: 'Q2 2025' },
    { code: 'AU', name: 'Australia', flag: '🇦🇺', available: false, coming: 'Q3 2025' },
    { code: 'CA', name: 'Canada', flag: '🇨🇦', available: false, coming: 'Q3 2025' },
    { code: 'NZ', name: 'New Zealand', flag: '🇳🇿', available: false, coming: 'Q4 2025' },
  ];
}

export default loadCountryRegulations;